<template>
  <div class="w-full flex border-t border-gray-700 flex-shrink-0" style="min-height: 96px; height: 96px; max-height: 96px;">
    <div v-if="roundData || isLoadingRound" class="w-full flex flex-col items-center">
      <div class="w-full flex bg-gray-900" style="min-height: 58px; height: 58px;">
        <button
          class="w-20 flex items-center justify-center focus:outline-snone border-r border-gray-700"
          :class="isLoadingRound ? 'opacity-50 cursor-not-allowed' : 'bg-primary-600'"
          :disabled="isLoadingRound"
          @keydown.space="(event) => event.preventDefault()"
          @click="$emit('togglePlay')"
        >
          <img v-if="!playDemo" class="h-4" src="/img/icons/play.svg" />
          <img v-else class="h-4" src="/img/icons/pause.svg" />
        </button>
        <button
          v-for="step in [-1, 1]"
          :key="step"
          class="w-10 flex items-center justify-center focus:outline-none border-r border-gray-700 text-white"
          :class="isLoadingRound ? 'opacity-50 cursor-not-allowed' : ''"
          :disabled="isLoadingRound"
          :title="step < 0 ? $t('viewer.timeline.step_back') : $t('viewer.timeline.step_forward')"
          @keydown.space="(event) => event.preventDefault()"
          @click="$emit('stepFrame', step)"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="step < 0 ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'" />
          </svg>
        </button>
        <button
          class="w-20 flex items-center justify-center focus:outline-none border-r border-gray-700 text-white"
          :class="isLoadingRound ? 'opacity-50 cursor-not-allowed' : ''"
          :disabled="isLoadingRound"
          @click="$emit('updateFrameRate')"
        >
          {{ frameRate / 64 }}x
        </button>
        <div class="w-full bg-gray-900 relative">
          <input
            id="default-range"
            type="range"
            step="1"
            min="0"
            @mousedown="$emit('sliderMouseDown')"
            @mouseup="$emit('sliderMouseUp')"
            :value="sliderFrame"
            @input="$emit('update:sliderFrame', Number($event.target.value))"
            :max="roundData ? roundData.frames.length : 0"
            :disabled="isLoadingRound"
            :class="['w-full h-full slider z-30 absolute pointer-events-auto', isLoadingRound ? 'opacity-50 cursor-not-allowed' : '']"
          />
          <div
            v-if="roundData && roundData.frames && roundData.frames.length > 0"
            class="h-full bg-gray-500 bg-opacity-30 absolute z-20 border-r-2 border-gray-300"
            :style="{
              width: `${(sliderFrame / roundData.frames.length) * 100}%`,
            }"
          />
          <div class="w-full h-full flex divide-x divide-gray-700 divide-dashed">
            <div v-for="index in 8" :key="index" class="h-full w-full" />
          </div>
          <div class="w-full h-full flex absolute top-0 pointer-events-none">
            <div
              v-if="showGrenadeGroups"
              v-for="grenadeGroup in grenadeGroups"
              :key="`grenade-${grenadeGroup[0].uniqueID}`"
              class="h-full border-gray-500 absolute flex justify-end select-none pointer-events-none"
              :style="{ width: `${getTickTime(grenadeGroup[0])}%` }"
            >
              <div class="z-50 group flex h-full items-center justify border-r-2 border-gray-500 border-dashed pointer-events-none">
                <div @click="$emit('selectGrenadeGroup', grenadeGroup)" class="h-6 w-6 bg-gray-500 group-hover:scale-105 cursor-pointer duration-300 translate-x-[13px] rounded-full text-white text-xs flex items-center justify-center outline outline-gray-900 pointer-events-auto">
                  {{ grenadeGroup.length }}
                </div>
              </div>
              <div class="absolute bg-gray-800 -translate-y-full -mt-2 py-2 translate-x-1/2 rounded-md border-gray-700 border grid gap-3 pointer-events-auto" v-if="selectedGrenadeGroupId === grenadeGroup[0].uniqueID">
                <div class="w-full flex gap-2 px-3 items-center" v-for="grenade in grenadeGroup" :key="grenade.uniqueID">
                  <h2 class="text-text-secondary text-sm">{{ moment.unix(getTimeOfTick(grenade.throwTick)).format('m:ss') }}</h2>
                  <h1 class="text-sm flex-1" :class="[grenade.throwerSide === 'CT' ? 'text-text-ct' : 'text-text-t']">
                    {{ getGrenadeThrowerName(grenade) }}
                  </h1>
                  <img class="max-w-[16px]" :src="`/img/icons/${grenade.grenadeType}.png`">
                  <h2
                    v-if="grenade.grenadeSimMode < 0"
                    class="text-primary-400 text-sm underline cursor-pointer hover:text-primary-300 duration-300"
                    v-clipboard:copy="`setpos ${grenade.throwerX} ${grenade.throwerY} ${grenade.throwerZ}; setang ${grenade.throwerViewY} ${grenade.throwerViewX}`"
                    v-clipboard:success="() => $emit('copySucceeded')">{{ $t('viewer.timeline.copy') }}</h2>
                  <h2
                    v-else
                    class="text-primary-400 text-sm underline cursor-pointer hover:text-primary-300 duration-300"
                    @click="$emit('launchGrenade', grenade)">{{ $t('viewer.timeline.launch') }}</h2>
                </div>
              </div>
            </div>
            <div
              v-if="showRoundInfo"
              v-for="kill in timelineKills"
              :key="`kill-timeline-${kill.tick}`"
              class="h-full border-red-500 absolute flex justify-end pointer-events-none"
              :style="{ width: `${getTickTime(kill)}%` }"
            >
              <div
                class="w-[1px] h-full absolute z-50 pointer-events-none"
                :style="kill.attackerSide === 'CT' ? { 'background-color': '#687cca' } : { 'background-color': '#e38617' }" />
            </div>
            <div v-if="timelineEvents.plantEvent && showRoundInfo">
              <div class="h-full absolute flex justify-end pointer-events-none" :style="{ width: `${getTickTime(timelineEvents.plantEvent)}%` }">
                <div class="z-25 flex h-full items-center justify border-r border-danger-500 border-dashed pointer-events-none">
                  <div class="h-6 w-6 bg-danger-500 translate-x-3 rounded-md text-white text-xs flex items-center justify-center outline outline-gray-900 pointer-events-none">
                    <img class="brightness-200 p-1" src="../../assets/icons/c4.svg">
                  </div>
                </div>
              </div>
            </div>
            <div v-if="timelineEvents.defuseEvent && showRoundInfo">
              <div class="h-full border-red-500 absolute flex justify-end pointer-events-none" :style="{ width: `${getTickTime(timelineEvents.defuseEvent)}%` }">
                <div class="z-25 flex h-full items-center justify border-r border-text-ct border-dashed pointer-events-none">
                  <div class="h-6 w-6 bg-text-ct translate-x-3 rounded-md text-white text-xs flex items-center justify-center outline outline-gray-900 pointer-events-none">
                    <img class="brightness-200" src="../../assets/icons/defuser.svg">
                  </div>
                </div>
              </div>
            </div>
            <div v-if="timelineEvents.explodeEvent && showRoundInfo">
              <div class="h-full border-red-500 absolute flex justify-end pointer-events-none" :style="{ width: `${getTickTime(timelineEvents.explodeEvent)}%` }">
                <div class="z-25 flex h-full items-center justify border-r border-danger-500 border-dashed pointer-events-none">
                  <div class="h-6 w-6 bg-danger-500 translate-x-3 rounded-md text-white text-xs flex items-center justify-center outline outline-gray-900 pointer-events-none">
                    <img class="brightness-200 p-1" src="../../assets/icons/explosion.svg">
                  </div>
                </div>
              </div>
            </div>
            <div v-if="timelineEvents.roundEndEvent && showRoundInfo">
              <div class="h-full absolute flex justify-end z-[60] pointer-events-none" :style="{ width: `${getTickTime(timelineEvents.roundEndEvent)}%` }">
                <div
                  class="z-[60] flex h-full items-center justify border-r border-dashed pointer-events-none"
                  :class="[timelineEvents.roundEndEvent.winning_team === 2 ? 'border-[#e38617]' : 'border-[#687cca]']">
                  <div
                    class="h-6 w-6 translate-x-3 rounded-md text-white text-xs flex items-center justify-center outline outline-gray-900 z-[60] pointer-events-none"
                    :class="[timelineEvents.roundEndEvent.winning_team === 2 ? 'bg-[#e38617]' : 'bg-[#687cca]']">
                    <img
                      v-if="getRoundEndReasonIcon(timelineEvents.roundEndEvent.winning_reason)"
                      class="brightness-200 p-1"
                      :src="`/img/icons/${getRoundEndReasonIcon(timelineEvents.roundEndEvent.winning_reason)}.svg`">
                    <span v-else class="text-xs">{{ timelineEvents.roundEndEvent.winning_team === 2 ? 'T' : 'CT' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="w-full flex bg-gray-950 items-center flex-row" style="min-height: 38px; height: 38px;">
        <div
          class="h-full items-center flex justify-center text-center border-gray-700 cursor-pointer flex-1 border-b-4 -pb-4"
          :class="[
            roundWinner(index) === 2 ? 'text-[#e38617] border-b-[#e38617]' : 'text-[#687cca] border-b-[#687cca]',
            currentRound == index ? 'bg-gray-800' : 'border-b-gray-950 border-b-transparent',
          ]"
          @click="$emit('selectRound', index)"
          v-for="index in totalRounds"
          :key="index"
        >
          {{ index }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from 'moment';

const props = defineProps({
  roundData: Object,
  playDemo: Boolean,
  frameRate: Number,
  sliderFrame: Number,
  showGrenadeGroups: Boolean,
  showRoundInfo: Boolean,
  grenadeGroups: Array,
  timelineKills: Array,
  timelineEvents: Object,
  selectedGrenadeGroupId: [String, Number],
  currentRound: Number,
  totalRounds: Number,
  getTickTime: Function,
  getTimeOfTick: Function,
  getGrenadeThrowerName: Function,
  getRoundEndReasonIcon: Function,
  roundWinner: Function,
  isLoadingRound: Boolean,
});

defineEmits(['togglePlay', 'stepFrame', 'updateFrameRate', 'sliderMouseDown', 'sliderMouseUp', 'update:sliderFrame', 'selectGrenadeGroup', 'copySucceeded', 'launchGrenade', 'selectRound']);
</script>

<style scoped>
.slider {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 1px;
  height: 1px;
  background: transparent;
  cursor: pointer;
  border: none;
  opacity: 0;
}

.slider::-moz-range-thumb {
  width: 1px;
  height: 1px;
  background: transparent;
  cursor: pointer;
  border: none;
  opacity: 0;
}
</style>
